import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Home, KeyRound, Landmark, LogOut, Moon, Sun, UserCog, Users } from 'lucide-react';
import { useState } from 'react';
import type { GuardianshipEntry, HomePageOption, MemberResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { availableHomePageOptions, HOME_PAGE_LABELS } from '../lib/home-page';
import { useTheme } from '../lib/theme';
import { Alert, Button, Input } from './ui';
import { EditAccountModal } from './EditAccountModal';

/** Accessible on/off switch — a plain styled `<button role="switch">` rather
 * than pulling in a UI library for one control. */
function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-brand-accent' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

/** Expandable "Passwort ändern" form — collapsed by default so the menu
 * stays short; requires the current password (unlike the token-based
 * "Passwort vergessen" flow), see `AuthService.changePassword()`. */
function ChangePasswordForm() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [justChanged, setJustChanged] = useState(false);

  const change = useMutation({
    mutationFn: () => api('/auth/password/change', { method: 'POST', body: { currentPassword, newPassword } }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setJustChanged(true);
      setTimeout(() => setJustChanged(false), 5000);
    },
  });

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = currentPassword.length > 0 && newPassword.length >= 10 && !mismatch;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
      >
        <KeyRound size={15} className="shrink-0 text-slate-400" />
        Passwort ändern
      </button>
    );
  }

  return (
    <form
      className="space-y-2 rounded-md bg-slate-50 p-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) change.mutate();
      }}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <KeyRound size={15} className="shrink-0 text-slate-400" />
        Passwort ändern
      </div>
      <Input
        type="password"
        placeholder="Aktuelles Passwort"
        autoComplete="current-password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
      />
      <Input
        type="password"
        placeholder="Neues Passwort (mind. 10 Zeichen)"
        autoComplete="new-password"
        minLength={10}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <Input
        type="password"
        placeholder="Neues Passwort bestätigen"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      {mismatch && <p className="text-xs text-red-600">Die Passwörter stimmen nicht überein.</p>}
      {change.isError && (
        <Alert>{change.error instanceof ApiError ? change.error.message : 'Ändern fehlgeschlagen.'}</Alert>
      )}
      {justChanged && (
        <Alert kind="success">
          Passwort geändert. Andere angemeldete Geräte wurden abgemeldet.
        </Alert>
      )}
      <div className="flex justify-end gap-2 pt-0.5">
        <Button
          type="button"
          variant="ghost"
          className="px-2.5 py-1.5 text-xs"
          onClick={() => {
            setOpen(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
          }}
        >
          Abbrechen
        </Button>
        <Button type="submit" className="px-2.5 py-1.5 text-xs" disabled={!canSubmit || change.isPending}>
          {change.isPending ? 'Speichert …' : 'Speichern'}
        </Button>
      </div>
    </form>
  );
}

/** "Startseite" (§ Nutzeranfrage) — pro Benutzerkonto, global über alle
 * Vereine hinweg gespeichert (`users.home_page`), angewendet von
 * `resolveHomePagePath()` direkt nach dem Login (`LoginPage.tsx`). Das
 * Auswahlfeld zeigt nur Seiten, die der Benutzer im gerade aktiven Verein
 * auch tatsächlich sehen darf (`availableHomePageOptions()`) — dieselbe
 * Prüfung, die auch der Login-Redirect selbst anwendet, falls sich die
 * Berechtigungen zwischen Setzen und nächstem Login ändern. */
function HomePageSelect() {
  const { me, refresh } = useAuth();
  const options = me ? availableHomePageOptions(me) : [];
  const set = useMutation({
    mutationFn: (homePage: HomePageOption | null) =>
      api('/auth/me/home-page', { method: 'PATCH', body: { homePage } }),
    onSuccess: () => void refresh(),
  });

  if (!me || options.length <= 1) return null;

  return (
    <div className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5">
      <span className="flex items-center gap-2 text-sm text-slate-700">
        <Home size={15} className="shrink-0 text-slate-400" />
        Startseite
      </span>
      <select
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900"
        value={me.user.homePage ?? 'dashboard'}
        disabled={set.isPending}
        onChange={(e) => set.mutate(e.target.value === 'dashboard' ? null : (e.target.value as HomePageOption))}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {HOME_PAGE_LABELS[option]}
          </option>
        ))}
      </select>
    </div>
  );
}

/** "Kann sich auch selbst anmelden" (§ Nutzeranfrage) — only relevant, and
 * only shown, for a guardian who actually has at least one linked child
 * (`GET /guardianships/mine`); a member with no wards is always offered as
 * themselves regardless of this flag, so the toggle would do nothing for
 * them. Both queries are `enabled: open` — no round trip until the menu is
 * actually opened. Mirrors the admin-editable checkbox on the member's
 * "Verknüpfte Kinder" card (MemberDetailPage.tsx) — same underlying field,
 * `members.self_registration_enabled`, either can set it. */
function SelfRegistrationToggle({ open }: { open: boolean }) {
  const qc = useQueryClient();
  const { data: guardianships } = useQuery({
    queryKey: ['profile', 'guardianships-mine'],
    queryFn: () => api<GuardianshipEntry[]>('/guardianships/mine', { orgScoped: true }),
    enabled: open,
  });
  const { data: mine } = useQuery({
    queryKey: ['profile', 'member-mine'],
    queryFn: () => api<MemberResponse>('/members/me', { orgScoped: true }),
    enabled: open,
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api<MemberResponse>('/members/me/self-registration', { method: 'PATCH', orgScoped: true, body: { enabled } }),
    onSuccess: (updated) => qc.setQueryData(['profile', 'member-mine'], updated),
  });

  if (!guardianships || guardianships.length === 0 || !mine) return null;

  return (
    <div className="flex items-center justify-between rounded-md px-2 py-1.5">
      <span className="flex items-center gap-2 text-sm text-slate-700">
        <Users size={15} className="shrink-0 text-slate-400" />
        Auch für mich selbst anmelden
      </span>
      <ToggleSwitch
        checked={mine.selfRegistrationEnabled}
        onChange={(v) => toggle.mutate(v)}
        label="Auch für mich selbst anmelden"
      />
    </div>
  );
}

/** "Verein wechseln" — Liste der Vereinsmitgliedschaften mit Haken beim
 * aktiven (§ Nutzeranfrage „Organisation/Verein wechseln … im Profilmenü")
 * — ersetzt den früheren, separaten `OrgSwitcher` im Header (eigenes
 * `<select>` auf Desktop, hinter dem jetzt entfernten Hamburger-Menü auf
 * Mobile). Bei genau einer Mitgliedschaft nur eine ruhige Textzeile statt
 * einer Auswahl, die ohnehin nichts zum Wechseln anböte. */
function OrgSwitcherSection({ onDone }: { onDone: () => void }) {
  const { me, selectOrg } = useAuth();
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);

  if (!me || me.memberships.length === 0) return null;

  if (me.memberships.length === 1) {
    return (
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-xs text-slate-500">
        <Landmark size={13} className="shrink-0 text-slate-400" />
        <span className="truncate">{me.memberships[0]!.organizationName}</span>
      </div>
    );
  }

  return (
    <div className="space-y-0.5 border-b border-slate-100 p-2">
      <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Verein wechseln</p>
      {me.memberships.map((m) => {
        const active = m.organizationId === me.activeOrganizationId;
        return (
          <button
            key={m.organizationId}
            type="button"
            disabled={active || switchingTo !== null}
            onClick={async () => {
              setSwitchingTo(m.organizationId);
              try {
                await selectOrg(m.organizationId);
                onDone();
              } finally {
                setSwitchingTo(null);
              }
            }}
            className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors disabled:cursor-default ${
              active ? 'bg-brand-accent/10 font-medium text-brand-accent' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            <span className="min-w-0 truncate">{m.organizationName}</span>
            {active ? (
              <Check size={15} className="shrink-0" />
            ) : switchingTo === m.organizationId ? (
              <span className="shrink-0 text-xs text-slate-400">wechselt …</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Avatar-Button oben rechts — gebündeltes Konto-/Sitzungsmenü: Verein
 * wechseln, "Konto bearbeiten" (eigene Stammdaten, `EditAccountModal.tsx`),
 * Dark-Mode-Schalter, Startseite, "Auch für mich selbst anmelden",
 * "Passwort ändern" und Abmelden. Optisch als eine zusammenhängende Karte
 * mit Kopfzeile (Avatar-Initiale, Name, E-Mail) und durch feine Trennlinien
 * gruppierten Abschnitten, statt einer losen Liste — moderneres,
 * "App-typisches" Kontomenü (vgl. Profilkarte unten in der Desktop-Sidebar).
 * Vereint seit § Nutzeranfrage „Hamburgermenü soll weg" zusätzlich zwei
 * Funktionen, die vorher im separaten, rein mobilen Hamburger-Dropdown
 * lebten (Verein wechseln, Abmelden) — dieses Menü ist jetzt die einzige
 * Stelle dafür, auf Mobile **und** Desktop gleichermaßen (der frühere
 * Header-eigene Logout-Button und das Dropdown-`<select>` sind entfallen,
 * siehe `AppShell.tsx`). */
export function ProfileMenu() {
  const { me, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);

  if (!me) return null;

  const displayName = me.user.firstName ?? me.user.email;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Benutzerprofil"
        aria-expanded={open}
        className="relative z-20 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/10 text-sm font-semibold text-header-text hover:bg-black/15"
      >
        {initial}
      </button>
      {open && (
        <>
          {/* Click-outside catcher — a plain full-viewport button behind the
           * panel, consistent with how modals in this app close on backdrop
           * click, without needing a click-outside hook/library. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-2 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center gap-3 bg-slate-50 px-4 py-3.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-base font-bold text-brand-accent">
                {initial}
              </span>
              <div className="min-w-0 flex-1 leading-tight">
                <p className="truncate text-sm font-semibold text-slate-800">{displayName}</p>
                {/* Nur eine zweite Zeile, wenn sie wirklich etwas Neues zeigt —
                 * ein Konto ohne hinterlegten Vornamen (z. B. ein reiner
                 * Plattformadmin) hätte sonst dieselbe E-Mail-Adresse zweimal
                 * untereinander stehen. */}
                {displayName !== me.user.email && (
                  <p className="truncate text-xs text-slate-500">{me.user.email}</p>
                )}
              </div>
            </div>

            <OrgSwitcherSection onDone={() => setOpen(false)} />

            <div className="space-y-1 border-b border-slate-100 p-2">
              <button
                type="button"
                onClick={() => {
                  setEditingAccount(true);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100"
              >
                <UserCog size={15} className="shrink-0 text-slate-400" />
                Konto bearbeiten
              </button>
              <div className="flex items-center justify-between rounded-md px-2 py-1.5">
                <span className="flex items-center gap-2 text-sm text-slate-700">
                  {theme === 'dark' ? (
                    <Moon size={15} className="shrink-0 text-slate-400" />
                  ) : (
                    <Sun size={15} className="shrink-0 text-slate-400" />
                  )}
                  Dark Mode
                </span>
                <ToggleSwitch checked={theme === 'dark'} onChange={toggleTheme} label="Dark Mode" />
              </div>
              <HomePageSelect />
              <SelfRegistrationToggle open={open} />
              <ChangePasswordForm />
            </div>

            <div className="p-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  void logout();
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <LogOut size={15} className="shrink-0" />
                Abmelden
              </button>
            </div>
          </div>
        </>
      )}
      {editingAccount && <EditAccountModal onClose={() => setEditingAccount(false)} />}
    </div>
  );
}

